import React, { useState, useEffect } from "react";
import axios,{AxiosRequestHeaders} from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../images/giphy.gif";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";
import ConfirmationModal from "../shared/ConfirmationModal";

interface Industry {
  id: number;
  name: string;
  industry_icon?: string;
}

const Industries = () => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<number | null>(null);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredIndustries, setFilteredIndustries] = useState<Industry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalIndustries, setTotalIndustries] = useState(0);
  const [pageSize] = useState(50);
  const headers: AxiosRequestHeaders = {
    'x-access-token': localStorage.getItem('jwttoken') || '',
  };
  const fetchIndustries = async () => {
    try {
      setLoader(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}industries/get-all-industries?page=${currentPage}&limit=${pageSize}`
      ,{headers});
      
      if (response.data.industries) {
        setIndustries(response.data.industries);
        setFilteredIndustries(response.data.industries); 
        setTotalPages(response.data.totalPages || 1);
        setTotalIndustries(response.data.totalIndustries || response.data.industries.length);
      } else {
        setIndustries(response.data);
        setFilteredIndustries(response.data);
        setTotalPages(Math.ceil(response.data.length / pageSize));
        setTotalIndustries(response.data.length);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch industries.");
      setFailureMessage("Failed to fetch industries. Please try again.");
      setShowFailureModal(true);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, [currentPage]); 

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === "") {
        setFilteredIndustries(industries);
      } else {
        const filtered = industries.filter((industry) =>
          industry.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredIndustries(filtered);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, industries]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalIndustries);

  const confirmDelete = (id: number) => {
  setModalMessage("Are you sure you want to delete this industry?");
  setPendingAction(id); // Save ID to use later
  setIsConfirmModal(true); // This flag tells modal that it's a confirmation modal
  setModalShow(true); // Show modal BEFORE calling delete
};

const handleConfirmation = async (isConfirmed: boolean) => {
  setModalShow(false);
  if (isConfirmed && pendingAction !== null) {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}industries/delete-industry/${pendingAction}`,
        { headers }
      );
      setSuccessMessage("Industry deleted successfully!");
      setShowSuccessModal(true);
      fetchIndustries();
    } catch (error) {
      setFailureMessage("Failed to delete industry. Please try again.");
      setShowFailureModal(true);
    } finally {
      setPendingAction(null); // Clear pending action
    }
  }
};


  const closeModal = () => {
    setModalShow(false);
    if (!isConfirmModal) {
      fetchIndustries();
    }
  };

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-black fw-bold">Industry Management</h2>
          <div className="d-flex gap-2 align-items-center" style={{width:"510px"}}>
          <span className="fw-bold text-secondary text-nowrap">{totalIndustries} results</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Name"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/industryadd" className="btn bg-info text-white">
              + Add Industry
            </Link>
          </div>
        </div>

        <div className="card shadow-sm border-0 rounded">
          <div className="card-header bg-info border-bottom py-3">
            <h5 className="mb-0 text-white">Industries List</h5>
          </div>
          <div className="card-body p-0">
              <div
                className="table-responsive"
                style={{ maxHeight: "500px", overflowY: "auto" }}
              >
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light text-center position-sticky top-0">
                    <tr>
                      <th>Sr No.</th>
                      <th>Industry Name</th>
                      <th>Industry Icon</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={10} className="text-center py-2">
                      <img
                        src={loaderimage}
                        alt="Loading..."
                        height="40"
                      />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={10} className="text-center text-danger fw-bold py-3">
                        <div className="alert alert-danger text-center m-3" role="alert">
                        Oops! Something went wrong. Please try again later.
                        </div>
                      </td>
                    </tr>
                  ) : filteredIndustries.length > 0 ? (
                    filteredIndustries.map((industry, index) => (
                      <tr key={industry.id} className="border-bottom">
                        <td className="text-center">{startEntry + index}</td>
                        <td>{industry.name}</td>
                        <td className="text-center">
                        {industry.industry_icon ? (
                           <i className={`${industry.industry_icon} fs-4`}></i>
                         ) : (
                         <span className="text-muted">No Icon</span>
                         )}
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Link
                              to={`/industryedit/${industry.id}`}
                              className="btn btn-sm btn-outline-warning"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => confirmDelete(industry.id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center text-muted fw-bold">
                        <div
                          className="alert alert-secondary text-center m-3"
                          role="alert"
                        >
                          No Industries Available
                        </div>
                      </td>
                    </tr>
                  )}
                  </tbody>
                </table>
              </div>
              <div className="position-sticky bottom-0 bg-white py-2 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted ps-3">
                    Showing {startEntry}-{endEntry} of {totalIndustries} entries
                  </div>
                  <nav>
                    <ul className="pagination mb-0 pe-3">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li 
                          key={index} 
                          className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
          </div>
        </div>

        <ConfirmationModal show={modalShow} modalMessage={modalMessage} onHide={closeModal} confirmation={isConfirmModal ? handleConfirmation : null} operationType="Delete" />
        <NewconfirmationModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} modalMessage={successMessage} operationType="OK" timeoutDuration={3000} />
        <AllFailureModal show={showFailureModal} onHide={() => setShowFailureModal(false)} modalMessage={failureMessage} operationType="OK" timeoutDuration={3000} />
      </div>
    </>
  );
};

export default Industries;
